import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import {
  attachClosestEdge,
  extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { disableNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview";
import { scrollJustEnoughIntoView } from "@atlaskit/pragmatic-drag-and-drop/element/scroll-just-enough-into-view";
import {
  autoScrollForElements,
  autoScrollWindowForElements,
} from "@atlaskit/pragmatic-drag-and-drop-auto-scroll/element";
// import { monitorForExternal } from "@atlaskit/pragmatic-drag-and-drop/external/adapter";
import { preventUnhandled } from "@atlaskit/pragmatic-drag-and-drop/prevent-unhandled";

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { configureStore } from "@reduxjs/toolkit";
import { useSearchParams } from "src/router";
import { motion, MotionStyle } from "motion/react";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Provider,
  useSelector as useUntypedSelector,
  useDispatch as useUntypedDispatch,
} from "react-redux";
import {
  createContext,
  useContext,
  useContextSelector,
} from "use-context-selector";
import {
  defaultConditionHeight,
  dragPreviewId,
  waterfallHeight,
} from "./visual/constants";
import { useFlags } from "launchdarkly-react-client-sdk";
import { captureException } from "src/lib/monitoring";

/**
 * TODOS:
 * - dragListIds are never cleaned up. They should be removed on deregistration
 *   except we potentially need them briefly after they are deregistered
 *   due to setTimeouts/animation lag. Should fix this
 */

type SourceData<Item> = {
  dragListId: string;
  index: number;
  /** For ghost rendering */
  originalHeight?: number;
  item: Item;
  id: string;
};

interface DropData {
  level: number;
  dragListId: string;
  index: number;
  dropPlacement: DropPlacement;
}

interface UseDraggableProps<Item> {
  index: number;
  item?: Item;
  id: string;
  /** Used to calculate the original height of the element so the ghost can match. Without waterfall */
  getContainerHeight?: () => number | undefined;
  disable?: boolean;
}

interface ItemLocation {
  dragListId: string;
  index: number;
}

interface DragStart<Item> {
  id: string;
  dragListId: string;
  index: number;
  item: Item;
  originalHeight: number;
}

interface Coords {
  x: number;
  y: number;
}

type DropPlacement = "before" | "after" | "split";

const allowedEdges = ["top" as const, "bottom" as const];

type Edge = (typeof allowedEdges)[number];

interface ExternalChangeHandlers {
  addAtIndex: (index: number, item: any) => void;
  remove: (id: string) => void;
}

interface DragList<DragListData> {
  id: string;
  data: DragListData;
}

interface State<Item, DragListData> {
  ghost?: ItemLocation;
  dragElement?: ItemLocation;
  dragElementOriginalHeight: number;
  dragLists: Record<string, DragList<DragListData>>;
  preview?: {
    item: Item;
    animationId: string;
    dragListData: DragListData;
  };
  enableAnimation: boolean;
  animatingElement?: string;
  animatingElementTimeout?: ReturnType<typeof setTimeout>;
  cursorCoordinates: Coords;
}

const initialState: State<any, any> = {
  dragLists: {},
  enableAnimation: true, // false
  dragElementOriginalHeight: defaultConditionHeight,
  cursorCoordinates: { x: 0, y: 0 },
};

const slice = createSlice({
  name: "draggable",
  initialState,
  reducers: {
    onDragStartInternal: (
      state,
      { payload }: PayloadAction<DragStart<any>>,
    ) => {
      const { dragListId, index, originalHeight, item, id } = payload;
      const dragList = state.dragLists[dragListId];

      state.ghost = { dragListId, index };
      state.dragElement = { dragListId, index };
      state.dragElementOriginalHeight = originalHeight;
      state.preview = {
        item,
        dragListData: dragList!.data,
        animationId: id,
      };
    },
    onDragOverDropTarget: (
      state,
      { payload }: PayloadAction<{ dropData: DropData; closestEdge: Edge }>,
    ) => {
      const { dropData, closestEdge } = payload;
      state.ghost = {
        dragListId: dropData.dragListId,
        index: getGhostIndex({
          index: dropData.index,
          closestEdge,
          dropPlacement: dropData.dropPlacement,
        }),
      };
    },
    onDropInternal: (
      state,
      {
        payload,
      }: PayloadAction<{
        drop?: { dragListId: string; id: string };
        animatingElementTimeout: ReturnType<typeof setTimeout>;
      }>,
    ) => {
      // Child layout animations need to be disabled if a drag list is dropped
      // so that the children are treated as part of the parent block, otherwise
      // they'll all trigger individual entry animations.
      const { drop, animatingElementTimeout } = payload;
      if (drop) {
        state.animatingElement = drop.id;
      }
      state.dragElement = undefined;
      state.ghost = undefined;
      state.preview = undefined;
      state.dragElementOriginalHeight = defaultConditionHeight;
      if (state.animatingElementTimeout) {
        clearTimeout(state.animatingElementTimeout);
      }
      state.animatingElementTimeout = animatingElementTimeout;
    },
    onDragListLayoutAnimationComplete: (
      state,
      { payload }: PayloadAction<{ id: string; animationId: string }>,
    ) => {
      const { id, animationId } = payload;
      if (
        state.animatingElement &&
        // We may briefly change animationIds to be different from id in order to connect certain animations,
        // like on drop the dropped item in the list briefly receives the preview animation id.
        (state.animatingElement === id || animationId === dragPreviewId)
      ) {
        state.animatingElement = undefined;
        if (state.animatingElementTimeout) {
          clearTimeout(state.animatingElementTimeout);
          state.animatingElementTimeout = undefined;
        }
      }
    },
    onDragListLayoutAnimationTimeout: (state) => {
      state.animatingElementTimeout = undefined;
      state.animatingElement = undefined;
    },
    registerDragList: (state, { payload }: PayloadAction<DragList<any>>) => {
      const dragList = state.dragLists[payload.id];
      if (dragList) {
        dragList.data = payload.data;
      } else {
        state.dragLists[payload.id] = {
          ...payload,
        };
      }
    },
    updateCursorCoordinates: (state, { payload }: PayloadAction<Coords>) => {
      const { x, y } = payload;
      // This offset controls where the cursor aligns on top of the drag preview while dragging
      state.cursorCoordinates = { x: x - 8, y: y - 40 };
    },
  },
});

const {
  onDragStartInternal,
  onDragOverDropTarget,
  onDropInternal,
  registerDragList,
  onDragListLayoutAnimationComplete,
  onDragListLayoutAnimationTimeout,
  updateCursorCoordinates,
} = slice.actions;

const onDrop =
  <Item,>(
    start: { dragListId: string; index: number; item: Item; id: string },
    dragListChangeHandlers?: React.MutableRefObject<
      Record<string, ExternalChangeHandlers>
    >,
  ) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const { ghost, dragLists } = getState();
    if (ghost) {
      const startDragList = dragLists[start.dragListId];
      const endDragList = dragLists[ghost.dragListId];

      if (
        typeof ghost.index === "number" &&
        startDragList &&
        endDragList &&
        start.index >= 0 &&
        // Don't move if we're dropping where the element started
        (start.dragListId !== ghost.dragListId || start.index !== ghost.index)
      ) {
        const addAtIndex =
          dragListChangeHandlers?.current?.[ghost.dragListId]?.addAtIndex;
        const remove =
          dragListChangeHandlers?.current?.[start.dragListId]?.remove;

        if (addAtIndex && remove) {
          remove(start.id);
          // Account for the deletion changing the destination index
          const adjustedDestinationIndex =
            ghost.dragListId === start.dragListId && ghost.index > start.index
              ? ghost.index - 1
              : ghost.index;

          addAtIndex(adjustedDestinationIndex, start.item);
        } else {
          captureException(
            new Error("Drag and drop: could not find external change handlers"),
          );
        }
      }
    }

    // By default, we block new drag starts until the animation from the previous one finishes to avoid
    // framer motion issues when multiple animations conflict with each other.
    // This is a fallback in case the animation doesn't signal completion, although I'm not sure if this will
    // still result in a bugged visual state.
    const animatingElementTimeout = setTimeout(() => {
      dispatch(onDragListLayoutAnimationTimeout());
    }, 1000);
    const drop = ghost
      ? { dragListId: ghost.dragListId, id: start.id }
      : undefined;
    dispatch(onDropInternal({ drop, animatingElementTimeout }));
  };

const store = configureStore({
  reducer: slice.reducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }),
  devTools: {
    actionsDenylist: [
      "draggable/onDrag$",
      "draggable/updateCursorCoordinates$",
      "draggable/onDragOverDropTarget",
    ],
  },
});

type RootState = ReturnType<typeof store.getState>;
type AppDispatch = typeof store.dispatch;
const useDispatch = useUntypedDispatch.withTypes<AppDispatch>();
const useSelector = useUntypedSelector.withTypes<RootState>();

const useOnDragAndOnDrop = () => {
  const { dragListChangeHandlers } = useDragTreeContext();
  const dispatch = useDispatch();
  useEffect(() => {
    return monitorForElements({
      onDragStart: () => {
        preventUnhandled.start();
      },
      onDrag: (e) => {
        // Continuously update ghost position on drag
        dispatch(
          updateCursorCoordinates({
            x: e.location.current.input.clientX,
            y: e.location.current.input.clientY,
          }),
        );
        // drop targets are ordered innermost first
        const dropTarget = e.location.current.dropTargets[0];
        if (dropTarget) {
          const dropData = dropTarget.data as unknown as DropData;
          const closestEdge = (extractClosestEdge(dropTarget.data) ??
            "bottom") as Edge;
          dispatch(onDragOverDropTarget({ dropData, closestEdge }));
        }
      },
      onDrop: ({ source }) => {
        dispatch(
          onDrop(source.data as SourceData<any>, dragListChangeHandlers),
        );
      },
    });
  }, [dispatch, dragListChangeHandlers]);
};

const DragHooks = () => {
  useOnDragAndOnDrop();
  return null;
};

/** For things that can't go into the redux store, mostly performance optimizations */
interface DragTreeContextValue {
  blockDragStartRef?: React.MutableRefObject<boolean>;
  dragListChangeHandlers?: React.MutableRefObject<
    Record<string, ExternalChangeHandlers>
  >;
  updateDragListChangeHandlers: (
    dragListId: string,
    changeHandlers: ExternalChangeHandlers,
  ) => void;
}

const dragTreeContext = createContext<DragTreeContextValue>({
  updateDragListChangeHandlers: () => {},
});

const useDragTreeContext = () => useContext(dragTreeContext);

interface DragTreeProps<Item, DragListData> {
  children?: React.ReactNode;
  renderPreview: (item: Item, dragListData: DragListData) => React.ReactNode;
}

export const DragTree = <Item, DragListData>(
  props: DragTreeProps<Item, DragListData>,
) => {
  const { children, renderPreview } = props;
  const blockDragStartRef = useRef(false);
  const dragListChangeHandlers = useRef<Record<string, ExternalChangeHandlers>>(
    {},
  );

  const updateDragListChangeHandlers = useCallback(
    (dragListId: string, changeHandlers: ExternalChangeHandlers) => {
      dragListChangeHandlers.current[dragListId] = changeHandlers;
    },
    [],
  );

  useEffect(() => {
    return autoScrollWindowForElements();
  }, []);

  return (
    <Provider store={store}>
      <dragTreeContext.Provider
        value={{
          blockDragStartRef,
          updateDragListChangeHandlers,
          dragListChangeHandlers,
        }}
      >
        <DragHooks />
        {children}
        <Preview renderPreview={renderPreview} />
      </dragTreeContext.Provider>
    </Provider>
  );
};

interface PreviewProps<Item, DragListData> {
  renderPreview: (item: Item, dragListData: DragListData) => React.ReactNode;
}

const Preview = <Item, DragListData>(
  props: PreviewProps<Item, DragListData>,
) => {
  const { renderPreview } = props;
  const preview = useSelector((state) => state.preview);
  const { x, y } = useSelector((state) => state.cursorCoordinates);
  const motionProps = useMotionProps();

  if (!preview) {
    return null;
  }

  const styles: MotionStyle = {
    display: "flex",
    flex: 1,
    minWidth: 0,
    // Position under cursor
    position: "fixed",
    // CRITICAL: Allows drop events to fire on elements underneath
    pointerEvents: "none",

    left: x,
    top: y,
    borderRadius: "var(--chakra-radii-xl)",
    boxShadow: "var(--chakra-shadows-md)",
    willChange: "transform",
    overflow: "hidden",
  };

  return createPortal(
    <motion.div
      layoutRoot
      layoutId={dragPreviewId}
      style={styles}
      {...motionProps}
      initial={false}
      layout="position"
    >
      {renderPreview(preview.item, preview.dragListData)}
    </motion.div>,
    document.getElementById("portalAnchor")!, // Ensure this element exists
  );
};

interface DragListContextValue<Item> {
  id?: string;
  /** Ghost should be placed AFTER this index */
  ghostIndex: number;
  /** Index of the item being dragged. -1 indicates no item in this list is being dragged. */
  draggingIndex: number;
  level: number;
  enableAnimation: boolean;
  onLayoutAnimationComplete: (args: {
    id?: string;
    animationId: string;
  }) => void;
  /** Immediate parent drag list id */
  parentId?: string;
  onDragStart: (
    id: string,
    index: number,
    item: Item,
    clientX: number,
    clientY: number,
    originalHeight: number,
  ) => void;
}

const DragListContext = createContext<DragListContextValue<any>>({
  draggingIndex: -1,
  level: -1,
  ghostIndex: -1,
  enableAnimation: true,
  onLayoutAnimationComplete: () => {},
  onDragStart: () => {},
});

const getGhostIndex = (hovered: {
  index: number;
  closestEdge: Edge;
  dropPlacement: DropPlacement;
}) => {
  const { index, closestEdge, dropPlacement } = hovered;
  switch (dropPlacement) {
    case "before":
      return index;
    case "after":
      return index + 1;
    case "split":
      switch (closestEdge) {
        case "top":
          return index;
        case "bottom":
          return index + 1;
        default:
          throw new Error(
            "Drag and drop unrecognized closest edge: " + closestEdge,
          );
      }
    default:
      throw new Error(
        "Drag and drop unrecognized drop placement: " + dropPlacement,
      );
  }
};

// TODO: differentiate between internal and external exposed props, see if we should expose a useDragTree
export const useDragList = <Item, ReturnValue>(
  selector: (value: DragListContextValue<Item>) => ReturnValue,
) => {
  const value = useContextSelector(DragListContext, selector);
  return value;
};

interface DragListProps<Item, DragListData> {
  children?: React.ReactNode;

  /** Level of nesting of this drag list from the root DragTree */
  level: number;

  /** Number of items in this drag list */
  count: number;

  addAtIndex: (index: number, newItem: Item) => void;

  remove: (id: string) => void;

  /** Can supplement item data for things like rendering the drag preview */
  data: DragListData;

  /** If not supplied, drag will be disabled for this list */
  id?: string;
}

export const DragList = <Item, DragListData>(
  props: DragListProps<Item, DragListData>,
) => {
  const {
    children,
    level,
    count,
    addAtIndex,
    remove,
    data,
    id: dragListId,
  } = props;
  const dispatch = useDispatch();
  const dragElement = useSelector((state) => state.dragElement);
  const ghostIndex = useSelector((state) => {
    return dragListId != null && state.ghost?.dragListId === dragListId
      ? state.ghost.index
      : -1;
  });
  const parentId = useDragList((state) => state.id);
  const parentEnableAnimation = useDragList((state) => state.enableAnimation);
  const dragTreeEnableAnimation = useSelector((state) => state.enableAnimation);
  const { updateDragListChangeHandlers, blockDragStartRef } =
    useDragTreeContext();
  const animatingElement = useSelector((state) => state.animatingElement);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (dragListId) {
      dispatch(registerDragList({ id: dragListId, data }));
    }
  }, [count, dragListId, dispatch, data]);

  useEffect(() => {
    if (dragListId) {
      updateDragListChangeHandlers(dragListId, {
        addAtIndex,
        remove,
      });
    }
  }, [addAtIndex, updateDragListChangeHandlers, dragListId, remove]);

  const onLayoutAnimationComplete = useCallback(
    (args: { id?: string; animationId: string }) => {
      const { id, animationId } = args;
      if (blockDragStartRef && blockDragStartRef.current) {
        blockDragStartRef.current = false;
      }
      if (id) {
        dispatch(onDragListLayoutAnimationComplete({ id, animationId }));
      }
    },
    [dispatch, blockDragStartRef],
  );

  const onDragStart = useCallback(
    (
      id: string,
      index: number,
      item: Item,
      x: number,
      y: number,
      originalHeight: number,
    ) => {
      if (!dragListId) return;

      dispatch(updateCursorCoordinates({ x, y }));
      if (blockDragStartRef) {
        blockDragStartRef.current = true;
      }
      dispatch(
        onDragStartInternal({ dragListId, index, item, originalHeight, id }),
      );
    },
    [dragListId, dispatch, blockDragStartRef],
  );

  const enableAnimation =
    dragTreeEnableAnimation &&
    parentEnableAnimation &&
    animatingElement !== dragListId;

  return (
    <DragListContext.Provider
      value={{
        id: dragListId,
        parentId,
        /** -1 indicates there is no ghost element */
        ghostIndex,
        level,
        draggingIndex:
          dragListId && dragElement?.dragListId === dragListId
            ? dragElement.index
            : -1,
        enableAnimation,
        onLayoutAnimationComplete,
        onDragStart,
      }}
    >
      {children}
    </DragListContext.Provider>
  );
};

interface UseDragGhostHeightProps {
  includeWaterfall?: boolean;
}

export const useDragGhostHeight = ({
  includeWaterfall,
}: UseDragGhostHeightProps = {}) => {
  const height = useSelector((state) => state.dragElementOriginalHeight);
  if (includeWaterfall) {
    return height + waterfallHeight;
  }
  return height;
};

export const useDragScroll = () => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = ref.current;

    if (element) {
      return autoScrollForElements({
        element,
      });
    }
    return undefined;
  });
  return ref;
};

const noMotionProps = { initial: false };

const createMotionProps = (opts: {
  s?: string | null;
  d?: string | null;
  m?: string | null;
}) => ({
  layout: "position" as const,
  initial: false,
  transition: {
    type: "spring" as const,
    stiffness: opts.s ? parseInt(opts.s) : 200,
    damping: opts.d ? parseInt(opts.d) : 25,
    mass: opts.m ? parseInt(opts.m) : 0.2,
  },
});

/** Generic props for sub elements wrapped in motion.div to assist with auto animations */
export const useMotionProps = ():
  | ReturnType<typeof createMotionProps>
  | typeof noMotionProps => {
  const [search] = useSearchParams();
  const s = search.get("s");
  const d = search.get("d");
  const m = search.get("m");
  const motionProps = useMemo(() => createMotionProps({ s, m, d }), [s, d, m]);
  return useSelector((state) => state.enableAnimation)
    ? motionProps
    : noMotionProps;
};

export const useDraggable = <Item,>(props: UseDraggableProps<Item>) => {
  const { index, item, disable, getContainerHeight, id } = props;
  const ref = useRef<HTMLDivElement>(null);
  const dragListId = useDragList((state) => state.id);
  const onDragStart = useDragList((state) => state.onDragStart);
  const { blockDragStartRef } = useDragTreeContext();
  const { queryBuilderDrag } = useFlags();

  useEffect(() => {
    const element = ref?.current;

    if (element && item && dragListId) {
      // We should only have drag start events here. onDrag and onDrop must happen in the root monitor
      // because the original drag element gets swapped out with the ghost on drag start
      return draggable({
        element,
        canDrag: () => {
          if (dragListId && blockDragStartRef?.current) {
            return false;
          }
          if (!queryBuilderDrag) {
            return false;
          }
          return !disable;
        },
        onGenerateDragPreview: ({ nativeSetDragImage, source }) => {
          // Native drag previews have properties we don't want, like forced translucency on Windows
          // Instead, we manage the preview ourselves through a portal
          disableNativeDragPreview({ nativeSetDragImage });
          scrollJustEnoughIntoView({ element: source.element });
        },
        onDragStart: ({ location }) => {
          const height = getContainerHeight?.() ?? defaultConditionHeight;
          onDragStart(
            id,
            index,
            item,
            location.current.input.clientX,
            location.current.input.clientY,
            height,
          );
        },

        getInitialData: () =>
          ({
            dragListId,
            index,
            item,
            id,
          }) satisfies SourceData<Item>,
      });
    }
    return undefined;
  }, [
    index,
    dragListId,
    id,
    item,
    onDragStart,
    disable,
    blockDragStartRef,
    getContainerHeight,
    queryBuilderDrag,
  ]);

  return {
    ref,
    isDraggable: queryBuilderDrag && !!item && !disable,
  };
};

interface UseDropProps {
  /** Index of the item in the immediate parent list AFTER WHICH the dragged item should be placed on drop */
  index: number;

  /**
   * `before`: On drop, dragged item will be placed at index (before the item at index).
   * `after`:  On drop, dragged item will be placed after the index
   * `split`:  On drop, dragged item will be placed before or after index depending on
   *           if the cursor was hovering over the top or bottom half of the drop target
   * @default `before` */
  dropPlacement?: DropPlacement;

  disable?: boolean;
}

/** Declare a drop target. Multiple drop targets for the same drag list and index are allowed. */
export const useDropTarget = (props: UseDropProps) => {
  const { index, disable, dropPlacement = "before" } = props;
  const dragListId = useDragList((state) => state.id);
  const level = useDragList((state) => state.level);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (element && dragListId) {
      return dropTargetForElements({
        element,
        canDrop: (args) => {
          if (disable) {
            return false;
          }
          const sourceData = args.source.data as SourceData<unknown>;
          return (
            sourceData.dragListId !== dragListId || sourceData.index !== index
          );
        },
        getData: (args) =>
          attachClosestEdge(
            { level, index, dragListId, dropPlacement } satisfies DropData,
            {
              ...args,
              element,
              allowedEdges,
            },
          ),
      });
    }
    return undefined;
  }, [disable, index, level, dragListId, dropPlacement]);

  return {
    ref,
  };
};

export const useAnimatingElement = () =>
  useSelector((state) => state.animatingElement);
